import type { HttpError } from './makeRequest'
import type { UseWrappedMultipleRequestsStatus } from './useRequest'


interface Props<T extends any[] = any[]> {
  requestStatus: UseWrappedMultipleRequestsStatus<T>
  renderOnLoading: () => JSX.Element | null
  renderOnError: (response?: Response, httpProblem?: HttpError<any>['problem']) => JSX.Element | null
  renderOnSuccess: (data: T, responses: Response[]) => JSX.Element | null
}

const MultipleRequestsDependentRender = <T extends any[] = any[]>({
  requestStatus, renderOnLoading, renderOnError, renderOnSuccess,
}: Props<T>): JSX.Element | null => {
  switch (requestStatus.state) {
    case 'not-initiated':
      return null
    case 'loading':
      return renderOnLoading()
    // At the moment of writing, we never handle abortion distinctly from an error.
    case 'aborted':
      return renderOnError()
    case 'error':
      return renderOnError(requestStatus.response, requestStatus.httpProblem)
    case 'success':
      // Currently `makeRequest<T>` returns `T | undefined` however we certainly know that
      // the back end will return some data. We cannot remove `undefined` from the return
      // type of `makeRequest` because it is difficult to type it without runtime information.
      //
      // tl;dr: `getStatus.data` is not `undefined` if `getStatus.state === `success`
      // because that's how our API works.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return renderOnSuccess(requestStatus.data!, requestStatus.responses)
  }
}

export default MultipleRequestsDependentRender
